import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { API_URL, renovarSesion, tokenActual } from './cliente';

/** Qué consultas invalidar ante cada evento de dominio. */
const INVALIDACIONES: Record<string, string[][]> = {
  'cita.actualizada': [['agenda'], ['pendientes'], ['clientes'], ['resumen-hoy'], ['ventas']],
  'venta.por_cobrar': [['ventas'], ['agenda'], ['resumen-hoy']],
  'venta.pagada': [['ventas'], ['caja'], ['agenda'], ['resumen-hoy']],
  'venta.actualizada': [['ventas']],
  'caja.actualizada': [['caja'], ['resumen-hoy']],
  'inventario.alerta': [['productos'], ['resumen-hoy']],
  'notificacion.nueva': [['notificaciones']],
  'aprobacion.actualizada': [['aprobaciones'], ['resumen-hoy']],
  'terminal.estado': [['mp-terminales'], ['terminales-activas']],
  'cobro-tarjeta.resuelto': [['cobro-tarjeta'], ['caja']],
  'reembolso-tarjeta.resuelto': [['ventas']],
};

/**
 * Mantiene una conexión Socket.IO mientras hay sesión y refresca las vistas afectadas
 * cuando otro usuario (o la app del barbero) cambia algo.
 */
export function useTiempoReal(activo: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!activo) return;
    const socket: Socket = io(API_URL || window.location.origin, {
      path: '/ws',
      transports: ['websocket'],
      auth: (cb) => cb({ token: tokenActual() }),
    });

    for (const [evento, claves] of Object.entries(INVALIDACIONES)) {
      socket.on(evento, () => {
        for (const queryKey of claves) void queryClient.invalidateQueries({ queryKey });
      });
    }
    socket.on('venta.por_cobrar', () => toast.info('Un servicio terminó: hay una venta por cobrar'));
    socket.on('notificacion.nueva', (n: { titulo?: string; cuerpo?: string }) => {
      if (n.titulo) toast(n.titulo, { description: n.cuerpo });
    });
    // El token de acceso dura 15 min: si el servidor rechaza la conexión, se renueva y reintenta.
    socket.on('sesion.invalida', () => {
      void renovarSesion().then((ok) => ok && socket.connect());
    });

    return () => {
      socket.disconnect();
    };
  }, [activo, queryClient]);
}
